import React, { useEffect, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useSelector } from 'react-redux'
import v4 from 'uuid/v4'

import { DV_TAB_ASSISTANT_CUSTOMIZATIONS } from '../../../utils/TabNavigationConstants'
import URLsAssistants, { URL_ASSISTANT_DETAILS_CUSTOMIZATIONS } from '../../../URLSystem/Assistants/URLsAssistants'
import CustomizationsHeader from './CustomizationsHeader'
import AssistantDataProperty from './AssistantData/AssistantDataProperty'
import TypeOfAssistantProperty from './TypeOfAssistant/TypeOfAssistantProperty'
import InstructionsProperty from './Instructions/InstructionsProperty'
import ModelProperty from './Model/ModelProperty'
import ReasoningEffortProperty from './ReasoningEffort/ReasoningEffortProperty'
import RealtimeVoiceProperty from './RealtimeVoice/RealtimeVoiceProperty'
import ToolsAccessProperty from './ToolsAccess/ToolsAccessProperty'
import SkillsAccessProperty from './SkillsAccess/SkillsAccessProperty'
import DelegationDescriptionProperty from './DelegationDescription/DelegationDescriptionProperty'
import DelegationTargetsAccessProperty from './DelegationTargetsAccess/DelegationTargetsAccessProperty'
import McpServersAccessProperty from './McpServersAccess/McpServersAccessProperty'
import AddPreConfigTask from './PreConfigTasks/AddPreConfigTask'
import PreConfigTaskList from './PreConfigTasks/PreConfigTaskList'
import DeleteAssistant from './DeleteAssistant'
import { unwatch, watchProject } from '../../../utils/backends/firestore'
import ProjectHelper from '../../SettingsView/ProjectsSettings/ProjectHelper'
import DefaultProperty from './Default/DefaultProperty'
import ObjectRevisionHistory from '../../NotesView/NotesDV/PropertiesView/ObjectRevisionHistory'
import PermissionLevelProperty from './PermissionLevelProperty/PermissionLevelProperty'
import CopyAssistant from './CopyAssistant/CopyAssistant'
import { GLOBAL_PROJECT_ID } from '../../AdminPanel/Assistants/assistantsHelper'
import Button from '../../UIControls/Button'
import { translate } from '../../../i18n/TranslationService'
import HeartbeatAwakeTimeProperty from './Heartbeat/HeartbeatAwakeTimeProperty'
import HeartbeatIntervalProperty from './Heartbeat/HeartbeatIntervalProperty'
import HeartbeatModelProperty from './Heartbeat/HeartbeatModelProperty'
import HeartbeatReasoningEffortProperty from './Heartbeat/HeartbeatReasoningEffortProperty'
import HeartbeatChanceProperty from './Heartbeat/HeartbeatChanceProperty'
import HeartbeatWhatsAppProperty from './Heartbeat/HeartbeatWhatsAppProperty'
import HeartbeatPromptProperty from './Heartbeat/HeartbeatPromptProperty'
import HeartbeatStatusProperty from './Heartbeat/HeartbeatStatusProperty'
import InboundEmailModelProperty from './AssistantEmail/InboundEmailModelProperty'
import EmailSignatureProperty from './EmailSignature/EmailSignatureProperty'
import UpdateFromTemplate from './UpdateFromTemplate/UpdateFromTemplate'

export default function AssistantCustomizations({
    projectId,
    assistant,
    projectDetailedId,
    isInGlobalProject,
    isGlobalAsisstant,
    isAdmin = false,
}) {
    const selectedTab = useSelector(state => state.selectedNavItem)
    const smallScreen = useSelector(state => state.smallScreen)
    const isAnonymous = useSelector(state => state.loggedUser.isAnonymous)
    const loggedUser = useSelector(state => state.loggedUser)
    const defaultAssistantId = useSelector(state => state.defaultAssistant.uid)
    const defaultProjectId = useSelector(state => state.loggedUser.defaultProjectId)
    const [isFromTemplateProject, setIsFromTemplateProject] = useState(true)
    const [isOrganizeMode, setIsOrganizeMode] = useState(false)

    const writeBrowserURL = () => {
        if (selectedTab === DV_TAB_ASSISTANT_CUSTOMIZATIONS) {
            const data = { projectDetailedId, assistant, assistantId: assistant.uid }
            URLsAssistants.push(URL_ASSISTANT_DETAILS_CUSTOMIZATIONS, data, projectDetailedId, assistant.uid)
        }
    }

    const belongsToProject = isInGlobalProject || loggedUser.realProjectIds.includes(projectDetailedId)
    const canEditAssitant = !isGlobalAsisstant && !isAnonymous && belongsToProject && !assistant.fromTemplate

    const isGuide = !isInGlobalProject && loggedUser.realGuideProjectIds.includes(projectDetailedId)

    // Only block deletion if the assistant belongs to the default project
    // This allows deletion of project assistants even when they're set as default (assistant.isDefault)
    const isAssistantInDefaultProject =
        !isGlobalAsisstant && projectDetailedId === defaultProjectId && assistant.isDefault

    const hasTalkToAssistantTool =
        Array.isArray(assistant.allowedTools) && assistant.allowedTools.includes('talk_to_assistant')
    const canConfigureAssistantEmail = defaultAssistantId === assistant.uid

    const updateIsFromTemplate = templateProject => {
        setIsFromTemplateProject(templateProject ? templateProject.globalAssistantIds.includes(assistant.uid) : false)
    }

    // Toggle organize mode
    const toggleOrganizeMode = () => {
        setIsOrganizeMode(!isOrganizeMode)
    }

    // Render organize button
    const renderOrganizeButton = () => (
        <Button
            type="ghost"
            title={isOrganizeMode ? translate('Done') : translate('Organize')}
            icon={isOrganizeMode ? 'check' : 'move'}
            onPress={toggleOrganizeMode}
            disabled={!canEditAssitant}
        />
    )

    useEffect(() => {
        if (isGlobalAsisstant && isGuide) {
            const watcherKey = v4()

            const guide = ProjectHelper.getProjectById(projectDetailedId)
            watchProject(guide.parentTemplateId, updateIsFromTemplate, watcherKey)
            return () => {
                unwatch(watcherKey)
            }
        } else if (assistant.fromTemplate) {
            setIsFromTemplateProject(true)
        } else {
            setIsFromTemplateProject(false)
        }
    }, [isGuide, isGlobalAsisstant, projectDetailedId, assistant.fromTemplate])

    useEffect(() => {
        writeBrowserURL()
    }, [])

    return (
        <View style={localStyles.container}>
            <UpdateFromTemplate projectId={projectId} assistant={assistant} disabled={!canEditAssitant} />
            <CustomizationsHeader text="Basic Settings" containerStyle={{ marginBottom: 24 }} />
            <View style={[localStyles.properties, smallScreen ? localStyles.propertiesMobile : undefined]}>
                <View style={{ flex: 1, marginRight: smallScreen ? 0 : 72, width: smallScreen ? '100%' : '50%' }}>
                    <AssistantDataProperty disabled={!canEditAssitant} projectId={projectId} assistant={assistant} />
                    {isGlobalAsisstant &&
                        !isInGlobalProject &&
                        (!isGuide || isAdmin) &&
                        (defaultAssistantId !== assistant.uid || isAdmin) && (
                            <CopyAssistant
                                projectId={projectDetailedId}
                                assistant={assistant}
                                disabled={isAnonymous || !belongsToProject}
                                sourceProjectId={projectId}
                            />
                        )}
                    <PermissionLevelProperty
                        isGlobal={isGlobalAsisstant || isInGlobalProject}
                        fromTemplate={assistant.fromTemplate}
                    />
                </View>
                <View style={{ flex: 1, width: smallScreen ? '100%' : '50%' }}>
                    <DefaultProperty disabled={!canEditAssitant} assistant={assistant} projectId={projectId} />
                    <TypeOfAssistantProperty
                        disabled={!canEditAssitant || true}
                        projectId={projectId}
                        assistant={assistant}
                    />
                </View>
            </View>
            <CustomizationsHeader text="Assistant Settings" />
            <View style={[localStyles.properties, smallScreen ? localStyles.propertiesMobile : undefined]}>
                <View style={{ flex: 1, marginRight: smallScreen ? 0 : 72, width: smallScreen ? '100%' : '50%' }}>
                    <InstructionsProperty disabled={!canEditAssitant} projectId={projectId} assistant={assistant} />
                    <ModelProperty disabled={!canEditAssitant} projectId={projectId} assistant={assistant} />
                    <ReasoningEffortProperty disabled={!canEditAssitant} projectId={projectId} assistant={assistant} />
                </View>
                <View style={{ flex: 1, width: smallScreen ? '100%' : '50%' }}>
                    <RealtimeVoiceProperty disabled={!canEditAssitant} projectId={projectId} assistant={assistant} />
                    <ToolsAccessProperty
                        disabled={!canEditAssitant}
                        projectId={projectId}
                        // The project on screen, which is where a browsing allowlist belongs — see
                        // ToolsAccessWrapper. `projectId` stays the assistant's own project because
                        // that is where the assistant document is written.
                        configProjectId={projectDetailedId}
                        assistant={assistant}
                    />
                    <SkillsAccessProperty disabled={!canEditAssitant} projectId={projectId} assistant={assistant} />
                    <McpServersAccessProperty disabled={!canEditAssitant} projectId={projectId} assistant={assistant} />
                    {hasTalkToAssistantTool && (
                        <DelegationTargetsAccessProperty
                            disabled={!canEditAssitant}
                            projectId={projectId}
                            assistant={assistant}
                        />
                    )}
                    <DelegationDescriptionProperty
                        disabled={!canEditAssitant}
                        projectId={projectId}
                        assistant={assistant}
                    />
                </View>
            </View>
            {canConfigureAssistantEmail && (
                <>
                    <CustomizationsHeader text="Assistant email" />
                    <View style={[localStyles.properties, smallScreen ? localStyles.propertiesMobile : undefined]}>
                        <View
                            style={{
                                flex: 1,
                                marginRight: smallScreen ? 0 : 72,
                                width: smallScreen ? '100%' : '50%',
                            }}
                        >
                            <InboundEmailModelProperty
                                disabled={!canEditAssitant}
                                projectId={projectId}
                                assistant={assistant}
                            />
                        </View>
                        <View style={{ flex: 1, width: smallScreen ? '100%' : '50%' }}>
                            <EmailSignatureProperty
                                disabled={!canEditAssitant}
                                projectId={projectId}
                                assistant={assistant}
                            />
                        </View>
                    </View>
                </>
            )}
            <CustomizationsHeader text="Assistant heartbeat" />
            <View style={[localStyles.properties, smallScreen ? localStyles.propertiesMobile : undefined]}>
                <View style={{ flex: 1, marginRight: smallScreen ? 0 : 72, width: smallScreen ? '100%' : '50%' }}>
                    <HeartbeatAwakeTimeProperty
                        disabled={!canEditAssitant}
                        projectId={projectId}
                        assistant={assistant}
                    />
                    <HeartbeatIntervalProperty
                        disabled={!canEditAssitant}
                        projectId={projectId}
                        assistant={assistant}
                    />
                    <HeartbeatModelProperty disabled={!canEditAssitant} projectId={projectId} assistant={assistant} />
                    <HeartbeatReasoningEffortProperty
                        disabled={!canEditAssitant}
                        projectId={projectId}
                        assistant={assistant}
                    />
                    <HeartbeatChanceProperty
                        disabled={!canEditAssitant}
                        projectId={projectId}
                        assistant={assistant}
                        variant="replied"
                    />
                    <HeartbeatChanceProperty
                        disabled={!canEditAssitant}
                        projectId={projectId}
                        assistant={assistant}
                        variant="noReply"
                    />
                    <HeartbeatWhatsAppProperty
                        disabled={!canEditAssitant}
                        projectId={projectId}
                        assistant={assistant}
                    />
                </View>
                <View style={{ flex: 1, width: smallScreen ? '100%' : '50%' }}>
                    <HeartbeatStatusProperty projectId={projectId} assistant={assistant} />
                    <HeartbeatPromptProperty disabled={!canEditAssitant} projectId={projectId} assistant={assistant} />
                </View>
            </View>
            <CustomizationsHeader text="Assistant tasks" rightContent={canEditAssitant && renderOrganizeButton()} />
            <PreConfigTaskList
                disabled={!canEditAssitant}
                projectId={projectId}
                assistantId={assistant.uid}
                isOrganizeMode={isOrganizeMode && canEditAssitant}
            />
            {canEditAssitant && !isOrganizeMode && (
                <View style={{ marginRight: smallScreen ? 0 : 72, width: '100%' }}>
                    <AddPreConfigTask projectId={projectId} assistantId={assistant.uid} />
                </View>
            )}
            <ObjectRevisionHistory
                projectId={projectDetailedId}
                noteId={assistant.noteIdsByProject[projectDetailedId]}
            />
            {!isAnonymous && belongsToProject && !isFromTemplateProject && !isAssistantInDefaultProject && (
                <View style={localStyles.deleteButtonContainer}>
                    <DeleteAssistant
                        isGlobalAsisstant={isGlobalAsisstant}
                        projectId={projectDetailedId}
                        assistant={assistant}
                    />
                </View>
            )}
        </View>
    )
}

const localStyles = StyleSheet.create({
    container: {
        flex: 1,
        marginBottom: 92,
    },
    properties: {
        flexDirection: 'row',
    },
    propertiesMobile: {
        flexDirection: 'column',
    },
    deleteButtonContainer: {
        marginTop: 24,
    },
})
